import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, TrendingUp, Eye } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const label = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.toLocaleDateString("de-CH", { weekday: "short" })} ${d.getDate()}.`;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-gold/30 bg-anthracite px-3 py-2 text-xs text-cream shadow-xl">
      <p className="text-cream/60">{new Date(p.date + "T00:00:00").toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long" })}</p>
      <p className="font-semibold mt-0.5" style={{ color: "#E5C76B" }}>{p.count} {p.count === 1 ? "Besuch" : "Besuche"}</p>
    </div>
  );
};

const AnalyticsPanel = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/analytics/visits?days=7`, { headers: authHeader() });
      setData(res);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Analyse konnte nicht geladen werden.");
      setData({ days: [], total: 0 });
    } finally {
      setRefreshing(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  if (data === null) {
    return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;
  }

  const chartData = data.days.map((d) => ({ ...d, label: label(d.date) }));
  const peak = Math.max(0, ...data.days.map((d) => d.count));
  const avg = data.days.length ? Math.round((data.total / data.days.length) * 10) / 10 : 0;

  return (
    <div data-testid="analytics-panel">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
        <div>
          <span className="section-eyebrow">Statistik</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Webseiten-Besuche</h1>
          <p className="mt-2 text-sm text-cream/60">Anonyme Besuche der Startseite – letzte 7 Tage.</p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="analytics-refresh"
          className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-gold-soft transition-colors disabled:opacity-50 self-start"
        >
          <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Aktualisieren
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <div className="rounded-2xl border border-gold/30 bg-white/[0.04] px-6 py-5" data-testid="analytics-total">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "#C69C22" }}>
            <Eye size={15} /> Besuche (7 Tage)
          </div>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-cream">{data.total}</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-cream/50">
            <TrendingUp size={15} /> Ø pro Tag
          </div>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-cream">{avg}</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-cream/50">
            Spitzentag
          </div>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-cream">{peak}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-2xl border border-gold/15 bg-white/[0.02] p-5 pt-6" data-testid="analytics-chart">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "rgba(250,250,248,0.55)", fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: "rgba(250,250,248,0.45)", fontSize: 12 }} axisLine={false} tickLine={false} width={36} />
            <Tooltip cursor={{ fill: "rgba(198,156,34,0.08)" }} content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={56}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={i === chartData.length - 1 ? "#E5C76B" : "#C69C22"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {data.total === 0 && (
        <p className="text-sm text-cream/45 text-center mt-6" data-testid="analytics-empty">
          Noch keine Besuche erfasst. Sobald die Webseite besucht wird, erscheinen die Zahlen hier.
        </p>
      )}
    </div>
  );
};

export default AnalyticsPanel;
